/** LSC subscription pricing and customer-facing copy. */

/** Operator note: Stripe prices remain $19.99/mo and $199.99/yr via env price IDs — verify in Stripe dashboard before deploy. */
export const LSC_SUBSCRIPTION_PRODUCT_NAME = 'League Stream Center subscription';

/** @deprecated Use LSC_SUBSCRIPTION_PRODUCT_NAME — kept for internal plan_key references. */
export const LSC_FOUNDER_BETA_PRODUCT_NAME = LSC_SUBSCRIPTION_PRODUCT_NAME;

export const LSC_FOUNDER_BETA_MONTHLY_PRICE = '$19.99';
export const LSC_FOUNDER_BETA_MONTHLY_LABEL = '$19.99/mo';
export const LSC_FOUNDER_BETA_ANNUAL_PRICE = '$199.99';
export const LSC_FOUNDER_BETA_ANNUAL_LABEL = '$199.99/yr';

export const LSC_SUBSCRIPTION_VALUE_THEMES = [
  'Organize your league’s Twitch VODs in one league replay hub',
  'Give members a trusted place to find published league games',
  'Commissioner review before anything goes live in the archive',
  'Schedule, stream, and archive context in one workspace',
  'Setup support included for this plan',
] as const;

/** @deprecated Use LSC_SUBSCRIPTION_VALUE_THEMES */
export const LSC_SUBSCRIPTION_THEMES = LSC_SUBSCRIPTION_VALUE_THEMES;

/** @deprecated Use LSC_SUBSCRIPTION_VALUE_THEMES */
export const LSC_FOUNDER_BETA_THEMES = LSC_SUBSCRIPTION_VALUE_THEMES;

export const LSC_PRICING_HERO_COPY =
  'League Stream Center helps commissioners turn Twitch streams into a league replay hub — one place for members to find published league games, with commissioner review before anything goes live.';

export const LSC_PRICING_PLAN_NOTES = [
  'Replay availability depends on Twitch VOD retention.',
  'This plan focuses on league organization, review, and member access.',
  'Permanent storage and highlight tools are planned upgrades.',
] as const;

export const LSC_SUBSCRIPTION_LIMITATIONS = LSC_PRICING_PLAN_NOTES;

/** @deprecated Use LSC_SUBSCRIPTION_LIMITATIONS */
export const LSC_FOUNDER_BETA_LIMITATIONS = LSC_SUBSCRIPTION_LIMITATIONS;

export const LSC_PLAN_LIMITATIONS_HEADER = 'Plan limitations';

export const LSC_SUBSCRIPTION_FOOTNOTE =
  'Twitch VOD availability still depends on Twitch retention windows. This plan does not include permanent full-game storage or highlight reel creation yet.';

export const LSC_CHECKOUT_UNAVAILABLE_COPY =
  'Checkout is temporarily unavailable. Try again later or contact support.';

/** Internal/legacy fallback when lifecycle message is absent. */
export const LSC_CHECKOUT_DISABLED_MESSAGE = LSC_CHECKOUT_UNAVAILABLE_COPY;

export const LSC_BILLING_UI_DISABLED_MESSAGE =
  'Contact your League Owner for subscription access.';

export const LSC_ANNUAL_SAVINGS_COPY = 'Save about two months with annual billing.';

export const LSC_PRICING_SUMMARY_LINE_PAID =
  'League Stream Center: $19.99/month or $199.99/year — one league subscription.';

export const LSC_PRICING_SUMMARY_LINE =
  'League Stream Center: $19.99/month or $199.99/year — one league subscription.';

export const LSC_LANDING_PRICING_TEASER =
  'Organize your league’s Twitch streams into a reviewable archive. Commissioners review and publish the final archive. Setup requires your league schedule and Twitch member connections.';

export const LSC_BILLING_SUCCESS_TITLE = 'Subscription activated';

export const LSC_BILLING_SUCCESS_BODY =
  'Your subscription is active. Link it to your league to unlock setup and publishing tools.';

export const LSC_BILLING_CANCELED_BODY =
  'No subscription was created. You can return to pricing when you are ready.';

export const LSC_PAID_UNBOUND_RECOVERY_TITLE =
  'Your subscription is active, but it is not linked to a league yet.';

export const LSC_PAID_UNBOUND_RECOVERY_MESSAGE =
  'Create or select your league, then link your subscription from League Settings.';

export const LSC_PAID_UNBOUND_MULTI_LEAGUE_MESSAGE =
  'You have multiple leagues. Open League Settings for the league you want to link your subscription to.';

export function formatPaidLinkedSuccessMessage(leagueName: string): string {
  const trimmed = leagueName.trim();
  return trimmed
    ? `Your subscription is active for ${trimmed}.`
    : 'Your subscription is active for your league.';
}

export const LSC_BILLING_SUPPORT_HELP_LINE = 'Need help linking your subscription?';

export const LSC_BILLING_SUPPORT_EMAIL_SUBJECT = 'League Stream Center billing help';

export const LSC_BILLING_SUPPORT_EMAIL_BODY =
  'Describe your league and what you need help with (subscription linking, checkout, or billing status).';

export const LSC_BILLING_OWNER_MANAGED_COPY = 'Billing is managed by the League Owner.';

export const LSC_WIZARD_SUBSCRIPTION_INTRO_COPY =
  'Use your existing Founder Beta checkout to activate this league. This is the same monthly or annual subscription already available in billing settings — not a new billing product.';

export const LSC_WIZARD_SUBSCRIPTION_MANAGE_NOTE =
  'You can review subscription status anytime from billing or league settings after checkout completes.';

export function formatBillingPeriodEnd(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function isCompedEntitlement(
  entitlement?: { is_comped?: boolean; plan_key?: string; subscription_status?: string } | null
): boolean {
  if (!entitlement) return false;
  if (entitlement.is_comped) return true;
  const plan = (entitlement.plan_key || '').toLowerCase();
  if (plan.includes('internal') || plan.includes('comped')) return true;
  return (entitlement.subscription_status || '').toLowerCase() === 'comped';
}

export function billingCadenceLabel(cadence?: string | null): string {
  const normalized = (cadence || '').toLowerCase();
  if (normalized === 'monthly') return 'Monthly plan';
  if (normalized === 'annual') return 'Annual plan';
  if (normalized === 'comped') return 'Comped/Internal';
  return 'Subscription';
}

/** Customer-safe billing lifecycle copy (Task 14). Prefer API customer_message when present. */
export const LSC_BILLING_LIFECYCLE_COPY: Record<string, string> = {
  billing_active: 'Your subscription is active.',
  billing_trialing: 'Your subscription trial is active.',
  billing_comped: 'Complimentary access is active.',
  billing_unbound: 'Your subscription is active, but it is not linked to a league yet.',
  billing_past_due: 'Payment needs attention. Update your payment method to keep access active.',
  billing_canceled: 'Your subscription is canceled. Resubscribe to restore paid access.',
  billing_incomplete: 'Checkout is incomplete. Finish checkout to activate your subscription.',
  billing_unpaid: 'Payment failed. Update billing to restore paid access.',
  billing_checkout_required: 'Start a subscription to unlock paid league features.',
  billing_portal_available: 'You can manage your subscription in the billing portal.',
  billing_portal_unavailable: 'Billing portal is unavailable right now. Please try again later.',
  billing_recovery_available: 'Link your subscription to a league to finish setup.',
  billing_enforcement_disabled: 'Billing enforcement is not required on this environment.',
  billing_unknown_review_required:
    'Subscription status needs review. Contact support if access looks wrong.',
};

export function billingLifecycleCustomerCopy(mode?: string | null): string {
  const key = (mode || '').trim().toLowerCase();
  return LSC_BILLING_LIFECYCLE_COPY[key] || LSC_BILLING_LIFECYCLE_COPY.billing_unknown_review_required;
}

export function billingLifecycleHeadline(mode?: string | null): string | null {
  switch ((mode || '').trim().toLowerCase()) {
    case 'billing_active':
      return 'Subscription active';
    case 'billing_trialing':
      return 'Subscription trial active';
    case 'billing_comped':
      return 'Complimentary access active';
    case 'billing_unbound':
      return 'Subscription active — link your league';
    case 'billing_past_due':
      return 'Subscription — payment attention needed';
    case 'billing_canceled':
      return 'Subscription canceled';
    case 'billing_incomplete':
      return 'Checkout incomplete';
    case 'billing_unpaid':
      return 'Subscription unpaid';
    case 'billing_checkout_required':
      return 'Subscription not active';
    case 'billing_portal_unavailable':
      return 'Billing portal unavailable';
    case 'billing_enforcement_disabled':
      return 'Subscription billing unavailable';
    case 'billing_unknown_review_required':
      return 'Subscription needs review';
    default:
      return null;
  }
}
